import {
  Component,
  HostListener,
  Inject,
  Input,
  OnInit, PLATFORM_ID
} from '@angular/core';
import {Router} from "@angular/router";
import {
  EventItemType,
  Organiser,
  RequestFilter,
  RequestStatus,
  UserDetail, Request, Message, PagedFilter, Pages
} from "@model";
import {
  CONTACT_ROUTE,
  USER_PROFILE_DASHBOARD_ROUTE,
  EVENT_CALENDAR_ROUTE,
  FINISHED_EVENTS_ROUTE,
  LANDING_ROUTE,
  LIVE_RESULTS,
  LOGIN_ROUTE,
  ORGANISER_DASHBOARD,
  PRIVACY_POLICY_ROUTE,
  PRIVATE_EVENT_CALENDAR_ROUTE,
  PRIVATE_FINISHED_EVENTS_ROUTE,
  PRIVATE_LIVE_RESULTS,
  SIGNUP_ROUTE,
  USER_PROFILE_VIEW_ROUTE,
  USER_PROFILE_EDIT_ROUTE,
  USER_MESSAGES,
  SERIES_ROUTE, PRIVATE_SERIES_ROUTE
} from "src/app/app-routing.module";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {TranslocoService} from '@ngneat/transloco';
import {animate, state, style, transition, trigger} from "@angular/animations";
import {AuthenticationService, MessageService, RequestService, UserDetailService} from "@services";
import {AbstractComponent} from "../abstract.component";
import {NewOrganiserModalComponent} from "../new-organiser-modal/new-organiser-modal.component";
import {DOCUMENT, isPlatformBrowser} from "@angular/common";

@Component({
  selector: 'app-page-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  animations: [
    trigger('slideInOut', [
      state('in', style({
        opacity: 1,
        transform: 'translate3d(0, 60px, 0)'
      })),
      state('inLanding', style({
        opacity: 1,
        transform: 'translate3d(0, 0, 0)'
      })),
      state('out', style({
        opacity: 0,
        transform: 'translate3d(0, calc(-100% - 96px), 0)'
      })),
      transition('in => out', animate('300ms ease-out')),
      transition('out => in', animate('300ms ease-in')),
      transition('inLanding => out', animate('300ms ease-out')),
      transition('out => inLanding', animate('300ms ease-in'))
    ])]
})
export class PageHeaderComponent extends AbstractComponent implements OnInit {
  @Input() additionalClass: string | undefined;
  @Input() isLanding: boolean = false;

  currentUserDetails: UserDetail | undefined;
  currentUrl: string = "";
  public currentWindowWidth: number = 0;
  isMobileMenuVisible: boolean = false;
  unreadMessages: Message[] | undefined;

  constructor(private router: Router, public authenticationService: AuthenticationService,
              private modalService: NgbModal, private requestService: RequestService,
              private userService: UserDetailService, private translocoService: TranslocoService,
              private messageService: MessageService, @Inject(DOCUMENT) private document: Document,
              @Inject(PLATFORM_ID) private platformId: Object) {
    super();
    if (isPlatformBrowser(this.platformId)) {
      this.currentWindowWidth = window.innerWidth;
    }
  }

  ngOnInit(): void {
    this.currentUrl = this.document.location.href;

    this.subscriptions.push(this.userService.currentUserDetails.subscribe((userDetails: UserDetail | undefined): void => {
      this.currentUserDetails = userDetails;
    }));

    if (this.authenticationService.isAuthenticated()) {
      this.messageService.getUserMessages(false).subscribe((messages: Message[]) => {
        this.unreadMessages = messages;
      });
    }
  }

  goToMessages(messageFilter: boolean) {
    this.router.navigate([USER_MESSAGES], {queryParams: {read: messageFilter}});
  }

  toggleMobileMenuVisibility() {
    if (this.currentWindowWidth < 1400) {
      this.isMobileMenuVisible = !this.isMobileMenuVisible;
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.currentWindowWidth = event.target.innerWidth;
  }

  goToLogin() {
    this.router.navigate([LOGIN_ROUTE]);
  }

  goToSignup() {
    this.router.navigate([SIGNUP_ROUTE]);
  }

  goToFinished() {
    this.toggleMobileMenuVisibility();
    this.router.navigate([this.authenticationService.isAuthenticated() ? PRIVATE_FINISHED_EVENTS_ROUTE : FINISHED_EVENTS_ROUTE]);
  }

  goToLiveResults() {
    this.toggleMobileMenuVisibility();
    this.router.navigate([this.authenticationService.isAuthenticated() ? PRIVATE_LIVE_RESULTS : LIVE_RESULTS]);
  }

  goToRegistered() {
    this.toggleMobileMenuVisibility();
    this.router.navigate([USER_PROFILE_DASHBOARD_ROUTE], {queryParams: {typeOfRace: EventItemType.MY_REGISTERED}});
  }

  goToFavorites() {
    this.toggleMobileMenuVisibility();
    this.router.navigate([USER_PROFILE_DASHBOARD_ROUTE], {queryParams: {typeOfRace: EventItemType.MY_FAVORITE}});
  }

  goToMyFinished() {
    this.toggleMobileMenuVisibility();
    this.router.navigate([USER_PROFILE_DASHBOARD_ROUTE], {queryParams: {typeOfRace: EventItemType.MY_FINISHED}});
  }

  onClickToLogoImage() {
    if (this.isMobileMenuVisible) {
      this.toggleMobileMenuVisibility();
    }
    this.router.navigate([this.authenticationService.isAuthenticated() ? PRIVATE_EVENT_CALENDAR_ROUTE : LANDING_ROUTE]);
  }

  onEventCalendar() {
    this.toggleMobileMenuVisibility();
    this.router.navigate([this.authenticationService.isAuthenticated() ? PRIVATE_EVENT_CALENDAR_ROUTE : EVENT_CALENDAR_ROUTE]);
  }

  goToSeries() {
    this.toggleMobileMenuVisibility();
    this.router.navigate([this.authenticationService.isAuthenticated() ? PRIVATE_SERIES_ROUTE : SERIES_ROUTE]);
  }

  logout() {
    this.toggleMobileMenuVisibility();
    this.authenticationService.logout();
  }

  onOpenProfile() {
    this.toggleMobileMenuVisibility();
    this.router.navigate([USER_PROFILE_EDIT_ROUTE]);
  }

  goToMyTracey() {
    this.toggleMobileMenuVisibility();
    this.router.navigate([USER_PROFILE_VIEW_ROUTE]);
  }

  isRouteActive(route: string): boolean {
    var isActive: boolean = false;
    switch (route) {
      case "EVENT_CALENDAR":
        isActive = this.currentUrl.includes("eventCalendar");
        break;
      case "FINISHED":
        isActive = this.currentUrl.includes("finishedEvents");
        break;
      case "CONTACT":
        isActive = this.currentUrl.includes("contact");
        break;
      case "PRIVACY":
        isActive = this.currentUrl.includes("privacy");
        break;
      case "LIVE_RESULTS":
        isActive = this.currentUrl.includes("liveResults");
        break;
      case "SIGN_UP":
        isActive = this.currentUrl.includes("signup");
        break;
      case "MY_RACES":
        isActive = this.currentUrl.includes("userProfileDashboard");
        break;
      case "MY_REGISTERED":
        isActive = this.currentUrl.includes("userProfileDashboard");
        break;
      case "MY_FINISHED":
        isActive = this.currentUrl.includes("userProfileDashboard");
        break;
      case "USER_PROFILE_DASHBOARD_ROUTE":
        isActive = this.currentUrl.includes("userProfileDashboard");
        break;
      case "USER_PROFILE_EDIT":
        isActive = this.currentUrl.includes("userProfileEdit");
        break;
      case "USER_PROFILE_VIEW_ROUTE":
        isActive = this.currentUrl.includes("userProfileView");
        break;
      case "SERIES":
        isActive = this.currentUrl.includes("leagues");
        break;
    }
    return isActive;
  }

  goToPrivacyPolicy() {
    this.toggleMobileMenuVisibility();
    this.router.navigate([PRIVACY_POLICY_ROUTE]);
  }

  goToContact() {
    this.router.navigate([CONTACT_ROUTE]);
  }

  onOrganiserButtonClick() {
    if (this.authenticationService.isAdmin() || this.authenticationService.isOrganiser()) {
      this.goToOrganizerModule();
    } else {
      this.openOrganiserRequestModule();
    }
  }

  private goToOrganizerModule() {
    this.router.navigate([ORGANISER_DASHBOARD]);
  }

  private openOrganiserRequestModule() {
    this.requestService.filterRequests(
      new PagedFilter(new Pages(), new RequestFilter(RequestStatus.ON_HOLD, this.currentUserDetails?.userId))
    ).subscribe((request: Organiser[]) => {
      let organiserRequestSent: boolean = (request.length ?? 0) > 0;
      const modalRef: NgbModalRef = this.modalService.open(NewOrganiserModalComponent, {
        centered: true,
        windowClass: 'new-organiser-modal'
      });
      modalRef.componentInstance.title = this.translocoService.translate("request.header.title");
      modalRef.componentInstance.requestSent = organiserRequestSent;
      modalRef.result.then((organiser: Organiser): void => {
        this.requestService.sendOrganiserRequest(Request.createOrganiserRequest(organiser), () => {
        }, () => {
        });
      }, (_: any): void => {
        /// ignore
      });
    });
  }
}
